// One-time consent before face enrollment. "Agree" is stored on the server
// (POST /attendance/face/consent); "Not now" just closes.
import React, { useState } from "react";
import { ScanFace, ShieldCheck, ImageOff, Trash2, ExternalLink } from "lucide-react";
import RemotePunchDialog, { dialogColors } from "../RemotePunch/RemotePunchDialog";
import { faceApi } from "./faceApi";
import { faceText } from "./faceText";

const POINT_ICONS = [ShieldCheck, ImageOff, Trash2];

// TODO-legal: point at the final privacy notice once legal publishes it.
export const PRIVACY_NOTICE_URL = "/privacy";

export default function FaceConsentModal({ open, onAgreed, onClose, isDarkMode, language }) {
  const t = faceText(language);
  const c = dialogColors(isDarkMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const agree = async () => {
    setBusy(true);
    setError(null);
    try {
      await faceApi.consent();
      await onAgreed?.();
    } catch (err) {
      setError(err?.body?.messages?.[language] || err?.message || t.consentFailed);
    } finally {
      setBusy(false);
    }
  };

  const btn = (primary) => ({
    flex: "1 1 160px", padding: "12px 20px", borderRadius: 12,
    border: primary ? "none" : `1px solid ${c.border}`,
    background: primary ? c.primary : "transparent",
    color: primary ? "#fff" : c.text,
    fontSize: 15, fontWeight: 800, fontFamily: "inherit",
    cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1,
    boxShadow: primary ? `0 4px 14px ${c.primary}44` : "none",
  });

  return (
    <RemotePunchDialog
      open={open}
      onClose={busy ? undefined : onClose}
      title={t.consentTitle}
      subtitle={t.consentIntro}
      icon={ScanFace}
      dark={isDarkMode}
      language={language}
      maxWidth={520}
      closeLabel={t.close}
      footer={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" onClick={agree} disabled={busy} style={btn(true)} data-testid="face-consent-agree">
            {busy ? t.agreeing : t.agree}
          </button>
          <button type="button" onClick={onClose} disabled={busy} style={btn(false)} data-testid="face-consent-not-now">
            {t.notNow}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {t.consentPoints.map((text, i) => {
          const Icon = POINT_ICONS[i] || ShieldCheck;
          return (
            <div key={text} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "11px 13px", borderRadius: 12, background: c.soft, border: `1px solid ${c.border}` }}>
              <div style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: `${c.primary}1a`, color: c.primary }}>
                <Icon size={16} />
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.7, fontWeight: 600 }}>{text}</div>
            </div>
          );
        })}

        {/* TODO-legal: placeholder wording, clearly marked until legal signs off. */}
        <div style={{ marginTop: 4, padding: "12px 14px", borderRadius: 12, border: `1px dashed ${c.warning}`, background: `${c.warning}12`, fontSize: 13, lineHeight: 1.7, color: c.text }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: c.warning, marginBottom: 4 }}>TODO-legal</div>
          {t.consentLegal}
        </div>

        <a
          href={PRIVACY_NOTICE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", fontSize: 13, fontWeight: 700, color: c.primary, textDecoration: "none" }}
        >
          <ExternalLink size={14} /> {t.privacyLink}
        </a>

        {error && <p style={{ margin: 0, fontSize: 13, color: c.danger, fontWeight: 600 }}>{error}</p>}
      </div>
    </RemotePunchDialog>
  );
}
